import { Upload, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { s3FileName, uploadToS3 } from "../utils/s3";
import useS3Url from "../hooks/useS3Url";

const S3UploadButton = ({
  name = "Add Attachments",
  initValue,
  fullWidth,
  value,
  onChange,
  onUpload,
  maxUploads = 1,
  style,
  icon,
  disabled,
}) => {
  const accessToken = useSelector((state) => state.user.accessToken);
  useEffect(() => {
    if (initValue && initValue.length > 0) {
      setFileList(initValue);
    }
  }, [initValue]);
  // console.log("initValue", initValue)
  // console.log("value", value)
  const [fileList, setFileList] = useState(
    Array.isArray(initValue) && initValue.length > 0
      ? initValue
      : Array.isArray(value)
        ? value
        : []
  );
  const [uploading, setUploading] = useState(false);
  const requestCountRef = useRef(0);
  const resetTimeoutRef = useRef(null);

  const uploadFile = async (file) => {

    try {
      return await uploadToS3(file, accessToken); // S3 key, stored in the DB
    } catch (error) {
      console.error("Upload error:", error);
      return null;
    }
  };

  const handleChange = async ({ file }) => {
    if (file.status === "done") return;

    if (fileList.length >= maxUploads) {
      Swal.fire({
        icon: "warning",
        title: "Upload limit reached",
        text: `You can only upload up to ${maxUploads} file(s).`,
      });
      return;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: "File must be smaller than 10MB!",
      });
      return;
    }

    if (requestCountRef.current >= 10) {
      Swal.fire({
        icon: "warning",
        title: "Upload limit reached",
        text: "Maximum 10 uploads every 40 seconds. Please wait.",
      });
      return;
    }

    requestCountRef.current++;

    try {
      setUploading(true);
      const url = await uploadFile(file.originFileObj || file); // Upload the file and get the URL
      if (url) {
        setFileList(url); // ✅ Store the S3 key
        onChange?.(url); // ✅ Return the S3 key
        onUpload?.(url);
      }
    } catch (error) {
      console.error("Error handling upload:", error);
    } finally {
      setUploading(false);
    }

    if (!resetTimeoutRef.current) {
      resetTimeoutRef.current = setTimeout(() => {
        requestCountRef.current = 0;
        resetTimeoutRef.current = null;
      }, 40000);
    }
  };
  const handleRemove = () => {
    setFileList("");
    onChange?.("");
    onUpload?.("");
  };
  const fileHref = useS3Url(typeof fileList === "string" ? fileList : "");

  useEffect(() => {
    if (value !== null && value !== undefined) {
      setFileList(value);
    } else {
      setFileList("");
    }
  }, [value]);

  return (
    <div style={{ width: fullWidth ? "100%" : "auto" }}>
      <Upload
        disabled={!((fileList?.length || 0) < maxUploads) || disabled || uploading}
        style={{ width: "100%" }}
        multiple={maxUploads > 1}
        showUploadList={false}
        beforeUpload={() => false}
        onChange={handleChange}
      >
        <Button
          disabled={!((fileList?.length || 0) < maxUploads) || disabled}
          type="default"
          loading={uploading}
          style={{
            borderRadius: "4px",
            padding: "20px 30px",
            color: "#A9A9A9",
            ...style,
          }}
          icon={icon}
        >
          {uploading ? "Uploading..." : name}
        </Button>
      </Upload>

      {/* Only show file link if value is not null */}
      {value !== null &&
        fileList !== null &&
        fileList !== "" &&
        (!Array.isArray(fileList) ||
          (Array.isArray(fileList) && fileList.length > 0)) ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            margin: "5px 0",
            // width: "50%",
          }}
        >
          <a
            href={fileHref || undefined}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <p style={{ margin: 0, fontSize: "14px", cursor: "pointer" }}>
              📄{" "}
              {typeof fileList === "string" || typeof fileList === "object"
                ? (() => {
                    const fileName = s3FileName(String(fileList));
                    return fileName.length > 15 
                      ? `${fileName.substring(0, 15)}...` 
                      : fileName;
                  })()
                : "Unnamed File"}
            </p>
          </a>
          <CloseOutlined
            style={{
              cursor: disabled ? "not-allowed" : "pointer",
              color: disabled ? "#d9d9d9" : "red",
              pointerEvents: disabled ? "none" : "auto",
            }}
            onClick={() => !disabled && handleRemove(fileList)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default S3UploadButton;
